import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { routes } from "utilities/routing/generatedRoutes";
import { LastRefreshDotLabel } from "./LastRefreshDotLabel";
import { ExternalCalendarState } from "./models";
import { ExternalCalendarType } from "./ExternalCalendarType";
import { formatMonths } from "utilities";
import ExternalCalendarHistoryDropdown from "components/dropdowns/reactselect/ExternalCalendarHistoryDropdown";
import { ExternalEventsPanel } from "./externalEvents/ExternalEventsPanel";
import WebsiteLink from "components/Website/WebsiteLink";
import { ButtonPrimary } from "components/Atoms/Button";
import axios from "axios";
import { addInterceptors } from "../../../Scripts/common/initApi";
import { formatTimeZone, getTimeZoneFromIana } from "shared/utils/dateTimeUtils";

interface ExternalCalendarDetailSectionProps {
  externalCalendar: server.dto.ExternalCalendar
  refreshUrl: string
}

export const ExternalCalendarDetailSection = (props: ExternalCalendarDetailSectionProps) => {
  const [state, setState] = useState<ExternalCalendarState>({
    title: props.externalCalendar.title,
    artist: props.externalCalendar.artist,
    monthsInPast: props.externalCalendar.monthsInPast,
  });
  const { externalCalendar } = props;
  const [searchParams] = useSearchParams();

  const onValueChanged = (key: string, value: any) => {
    if (key === "title") {
      setState({ ...state, title: value });
    } else if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "monthsInPast") {
      setState({ ...state, monthsInPast: value });
    }
  }

  const getUpdateExternalCalendarDetailInstruction = (st: ExternalCalendarState): server.dto.UpdateExternalCalendarInstruction => ({
    externalCalendarId: props.externalCalendar.externalCalendarId,
    title: st.title,
    artistId: st.artist?.artistId,
    monthsInThePast: st.monthsInPast,
  });

  const axiosClient = axios.create();
  addInterceptors(axiosClient);

  const refreshMutation = useMutation({
    mutationFn: () => axiosClient.get<server.dto.ApiResponse>(props.refreshUrl, { systemOneApiConfig: { toastOnError: true } }),
    onSuccess: (resp) => {
      if (resp.data.hasErrors) return;
      window.location.href = routes.externalCalendar_Detail(externalCalendar.externalCalendarId).absolute;
    },
  });
  // Stays true after success so the button can't be clicked while the page reloads
  const isRefreshing = refreshMutation.isLoading || (refreshMutation.isSuccess && !refreshMutation.data.data.hasErrors);

  useEffect(() => {
    if (searchParams.get("refresh") === "true") {
      refreshMutation.mutate();
    }
  }, []);

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "externalCalendar",
      title: RESX.ExternalCalendar.resxExternalCalendar,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Name,
        render: () => <div className="flex-column gap">
          <div className="flex justify-space-between">
            <div>
              <div>{state.title}</div>
              <LastRefreshDotLabel item={externalCalendar} />
            </div>
            <div>
              <ButtonPrimary
                onClick={() => refreshMutation.mutate()}
                label={RESX.Buttons.btnRefresh}
                loading={isRefreshing}
                disabled={isRefreshing}
              />
            </div>
          </div>
        </div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.externalCalendar.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: state.title,
          submitChange: (title: string) => window.CoreApi.ExternalCalendar.Update(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              title,
            })
          ),
        },
      }, {
        key: "events",
        label: "Events",
        render: () => <div>
          <div>{externalCalendar.eventCount}</div>
          <div className="s1_small s1_gray">
            {RESX.ExternalCalendar.AppoinmentsFurtherInTheFutureThanXMonthsAreDiscarded.replace("{0}", externalCalendar.monthsInFuture.toString())}
          </div>
          <div className="s1_small s1_gray">
            {RESX.ExternalCalendar.AppointmentsOlderThanXMonthsAreDiscarded.replace("{0}", state.monthsInPast.toString())}
          </div>
        </div>
      }, {
        key: "monthsInPast",
        label: RESX.ExternalCalendar.History,
        render: () => <div>{formatMonths(state.monthsInPast)}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <ExternalCalendarHistoryDropdown
            value={value}
            onChange={(v) => {
              onChange(v, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: state.monthsInPast,
          submitChange: (monthsInPast: number) => window.CoreApi.ExternalCalendar.Update(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              monthsInPast,
            })
          ),
        },
      }, {
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        render: () => <CompanySidePanel type="artist"
          companyId={state.artist.artistId}
          trigger={<a>{state.artist.name}</a>}
          fallbackIfNoPermission={<>{state.artist.name}</>}
        />,
        inPageEdit: {
          hoverEdit: true,
          render: ({ onChange, cancel, value }: RenderEditOptions<server.dto.ArtistSmall>) => <div className="main-section-template-fields-padding">
            <ArtistModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
              securityScope={"limitByUserAccess"}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: state.artist,
          submitChange: (artist: server.dto.ArtistSmall) => window.CoreApi.ExternalCalendar.Update(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              artist,
            })
          ),
        }
      }, {
        key: "vendor",
        label: RESX.ExternalCalendar.Vendor,
        isEmpty: !externalCalendar.vendor,
        hidden: externalCalendar.type !== "iCalendar",
        render: () => <div>{externalCalendar.vendor}</div>,
      }, {
        key: "type",
        label: RESX.GeneralLabels.Type,
        render: () => <ExternalCalendarType type={externalCalendar.type} />,
      }, {
        key: "iCalendarUrl",
        label: "iCalendar URL",
        hidden: externalCalendar.type !== "iCalendar",
        render: () => <WebsiteLink website={externalCalendar.url} />
      }, {
        key: "timezone",
        label: RESX.DateTime.Timezone,
        isEmpty: !externalCalendar.ianaTimeZoneId,
        hidden: externalCalendar.type !== "googleCalendar",
        render: () => <div>{formatTimeZone(getTimeZoneFromIana(externalCalendar.ianaTimeZoneId, serverReference.culture.uiLocale))}</div>,
      }, {
        key: "createdBy",
        label: RESX.RecordInformation.CreatedBy,
        render: () => <UserWithAvatar user={externalCalendar.createdBy} />,
      }, {
        key: "account",
        label: RESX.Account.resxAccount,
        render: () => <div>{externalCalendar.client.name}</div>
      }]
    }, {
      type: "custom",
      key: "externalEvents",
      render: () => <ExternalEventsPanel
        key="externalEvents"
        externalCalendarId={props.externalCalendar.externalCalendarId}
      />
    }]}
  />
}